import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

/**
 * Registry of the portal-rendered modals (`components/common/Modals/Modal`) that are currently open.
 *
 * Those modals are plain `createPortal` nodes outside every MUI overlay's DOM subtree and unknown
 * to MUI's modal manager, so an overlay rendered underneath keeps enforcing its own focus trap and
 * steals every keystroke aimed at the modal on top. Overlays that trap focus read this
 * registry to stand down for as long as a portal modal is up.
 */
let openPortalModals = 0
const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const getSnapshot = () => openPortalModals > 0

/** Registers a portal modal as open for as long as `isOpen` holds. */
export const useRegisterPortalModal = (isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen) return

    openPortalModals++
    listeners.forEach((listener) => listener())

    return () => {
      openPortalModals--
      listeners.forEach((listener) => listener())
    }
  }, [isOpen])
}

/** Whether any portal modal is currently open. */
export const useHasOpenPortalModal = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

/**
 * Set on the root of a MUI overlay whose focus trap stands down while a portal modal is open. An accessible
 * `Modal` pulls back focus landing there, but leaves alone a MUI overlay that still traps it.
 */
export const YIELDS_FOCUS_ATTRIBUTE = 'data-yields-focus-to-portal-modal'

/**
 * Open accessible dialogs (`Modal isAccessibleDialog`), oldest first. Only the last one answers Escape and Tab,
 * so a confirmation opened over an editor closes alone and keeps focus to itself.
 */
let openAccessibleDialogs: readonly symbol[] = []

/** Puts an accessible dialog on the stack while `isOpen` holds; the returned function tells whether it is on top. */
export const useAccessibleDialogStack = (isOpen: boolean) => {
  const tokenRef = useRef(Symbol('accessible-dialog'))

  useEffect(() => {
    if (!isOpen) return
    const token = tokenRef.current
    openAccessibleDialogs = [...openAccessibleDialogs, token]

    return () => {
      openAccessibleDialogs = openAccessibleDialogs.filter(open => open !== token)
    }
  }, [isOpen])

  return useCallback(() => openAccessibleDialogs[openAccessibleDialogs.length - 1] === tokenRef.current, [])
}

/** Whether an accessible dialog is open: a plain `Modal` underneath leaves Escape to it. */
export const hasOpenAccessibleDialog = () => openAccessibleDialogs.length > 0
